import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { useAppDispatch, useAppSelector, useStyles } from '~/hooks';
import { desktopSelector } from '~/state/selectors';
import { setAIPaneScreen } from '~/state/reducers/desktopSlice';
import { Theme } from '~/theme';

import AIStrategiesList, { AIStrategiesListHandle } from './AIStrategiesList';
import StrategyDetails from './StrategyDetails';
import PaneWizard from './components/PaneWizard';

/**
 * The AI strategy feature as a desktop pane: the same three views the mobile stack hosts, but
 * driven by state instead of routes.
 *
 * Deliberately not a navigator. Every other desktop pane is a plain component, and a navigator
 * mounted here joins the root container's tree — which is what used to append the pane's route
 * onto /desktop and produce a URL that could not be restored.
 */
export default function AIStrategyPane() {
    const styles = useStyles(stylesGenerator);
    const dispatch = useAppDispatch();
    const { aiScreen } = useAppSelector(desktopSelector);
    const listRef = useRef<AIStrategiesListHandle>(null);

    const showList = useCallback(() => dispatch(setAIPaneScreen({ screen: 'list' })), [dispatch]);

    // Coming back to the list should pick up enable/discard/create done while it was hidden. The
    // mobile host gets this from a stack focus event; here the screen change is the event.
    useEffect(() => {
        if (aiScreen.screen === 'list') {
            listRef.current?.refresh();
        }
    }, [aiScreen.screen]);

    if (aiScreen.screen === 'details') {
        return (
            <View style={styles.container}>
                <StrategyDetails
                    key={aiScreen.strategyId}
                    strategyId={aiScreen.strategyId}
                    onExit={showList}
                    onOpenList={showList}
                    onClone={(id) => dispatch(setAIPaneScreen({ screen: 'create', cloneStrategyId: id }))}
                />
            </View>
        );
    }

    if (aiScreen.screen === 'create') {
        return (
            <View style={styles.container}>
                <PaneWizard
                    templateUuid={aiScreen.templateUuid}
                    cloneStrategyId={aiScreen.cloneStrategyId}
                    onClose={showList}
                    onCreated={(strategyId) =>
                        dispatch(
                            strategyId
                                ? setAIPaneScreen({ screen: 'details', strategyId })
                                : setAIPaneScreen({ screen: 'list' })
                        )
                    }
                />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <AIStrategiesList
                ref={listRef}
                onOpenStrategy={(strategy) => dispatch(setAIPaneScreen({ screen: 'details', strategyId: strategy.id }))}
                onCreate={(templateUuid) => dispatch(setAIPaneScreen({ screen: 'create', templateUuid }))}
                onClone={(strategyId) => dispatch(setAIPaneScreen({ screen: 'create', cloneStrategyId: strategyId }))}
            />
        </View>
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
    });
